var HyperLinkSupport = require("enyo-ui/HyperLinkSupport"),
    IconButton       = require("app/ui/IconButton"),
    get              = require("object-path").get,
    icons            = require("app/theme/icons"),
    kind             = require("enyo/kind");

/**
 * This button instructs the current media player to toggle between playing and
 * pausing if it is playing the same item as the one referenced by the button.
 *
 * If a different item is playing, the button navigates to the item.
 *
 * @ui
 * @class   app.MediaPlayer.PlayPauseNavButton
 * @extends app.ui.IconButton
 * @public
 */
module.exports = kind(/** @lends  app.MediaPlayer.PlayPauseNavButton.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.PlayPauseNavButton",

    /**
     * @private
     */
    kind: IconButton,

    /**
     * @private
     */
    classes: "app-media-player-play-pause-nav-button",

    /**
     * @private
     */
    mixins: [HyperLinkSupport],

    /**
     * @type  {string}
     * @public
     */
    theme: null,

    /**
     * @type  {?string}
     * @public
     */
    loadingCaption: null,

    /**
     * @type  {?string}
     * @public
     */
    loadingIcon: icons["app.MediaPlayer.PlayPauseNavButton.loading"],

    /**
     * @type  {?string}
     * @public
     */
    playingCaption: null,

    /**
     * @type  {?string}
     * @public
     */
    playingIcon: icons["app.MediaPlayer.PlayPauseNavButton.playing"],

    /**
     * @type  {?string}
     * @public
     */
    pausedCaption: null,

    /**
     * @type  {?string}
     * @public
     */
    pausedIcon: icons["app.MediaPlayer.PlayPauseNavButton.paused"],

    /**
     * @type  {?string}
     * @public
     */
    differentMediaCaption: null,

    /**
     * @type  {?string}
     * @public
     */
    differentMediaIcon: icons["app.MediaPlayer.PlayPauseNavButton.differentMedia"],

    /**
     * The mediaItem
     *
     * @type  {?Object}
     * @public
     */
    mediaItem: null,

    /**
     * @private
     */
    handlers: {
        ontap: "onHyperlinkTap"
    },

    /**
     * @private
     */
    observers: {
        update: ["mediaItem"]
    },

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);

            // Unlike many other components, we don't throttle the update cycle
            // of this one. We need changes to dimensions and visibility to be
            // instant, so parent components can respond and adjust their
            // layout.
            this.observe("app.$.i18nService.translation", this.update);
            this.observe("app.$.state.mediaPlayerReady", this.update);
            this.observe("app.$.state.mediaPlayerPlaying", this.update);
            this.observe("app.$.state.mediaPlayerPlayingAds", this.update);
            this.observe("app.$.state.mediaPlayerMediaId", this.update);
            this.update();
        };
    }),

    /**
     * @protected
     */
    update: function() {
        var $appState             = this.app.$.state,
            $i18nService          = this.app.$.i18nService,
            mediaPlayerReady      = $appState.get("mediaPlayerReady"),
            mediaPlayerPlaying    = $appState.get("mediaPlayerPlaying"),
            mediaPlayerPlayingAds = $appState.get("mediaPlayerPlayingAds"),
            mediaPlayerMediaId    = $appState.get("mediaPlayerMediaId"),
            mediaItem             = this.get("mediaItem");

        this.set("href", get(mediaItem, "links.player", null));

        // No media
        if (!mediaItem) {
            // Css flags
            this.setAttribute("data-status", "inactive");

            while (this.hasClass("loading")) {
                this.removeClass("loading");
            }

            while (this.hasClass("active")) {
                this.removeClass("active");
            }

            // Content
            this.hide();
            this.set("icon", null);
            this.set("caption", null);
        }

        // Media player is playing our media item
        else if (mediaPlayerMediaId === mediaItem.id) {

            // Media player is loading
            if (mediaPlayerReady === 0) {
                // Css flags
                this.setAttribute("data-status", "loading");
                !this.hasClass("loading") && this.addClass("loading");

                // Content
                this.set("icon", this.get("loadingIcon"));
                this.set("caption", this.get("loadingCaption"));
                this.set("tooltip", $i18nService.getString("app.MediaPlayer.PlayPauseNavButton.Tooltip.loading"));
                this.show();
            }

            // Media player is ready
            else if (mediaPlayerReady === 1) {
                // Css flags
                while (this.hasClass("loading")) {
                    this.removeClass("loading");
                }

                // Media player is playing
                if (mediaPlayerPlaying || mediaPlayerPlayingAds) {
                    // Css flags
                    this.setAttribute("data-status", "playing");
                    !this.hasClass("active") && this.addClass("active");

                    // Content
                    this.set("icon", this.get("playingIcon"));
                    this.set("caption", this.get("playingCaption"));
                    this.set("tooltip", $i18nService.getString("app.MediaPlayer.PlayPauseNavButton.Tooltip.playing"));
                    this.show();
                }

                // Media player is paused
                else {
                    // Css flags
                    this.setAttribute("data-status", "paused");

                    while (this.hasClass("active")) {
                        this.removeClass("active");
                    }

                    // Content
                    this.set("icon", this.get("pausedIcon"));
                    this.set("caption", this.get("pausedCaption"));
                    this.set("tooltip", $i18nService.getString("app.MediaPlayer.PlayPauseNavButton.Tooltip.paused"));
                    this.show();
                }
            }

            // No media player
            else {
                // Css flags
                this.setAttribute("data-status", "inactive");

                while (this.hasClass("active")) {
                    this.removeClass("active");
                }

                // Content
                this.set("icon", this.get("pausedIcon"));
                this.set("caption", this.get("pausedCaption"));
                this.set("tooltip", $i18nService.getString("app.MediaPlayer.PlayPauseNavButton.Tooltip.playing"));
                this.show();
            }
        }

        // Media player is not playing our media item
        else {
            // Css flags
            this.setAttribute("data-status", "different-media");

            while (this.hasClass("active")) {
                this.removeClass("active");
            }

            // Content
            this.set("icon", this.get("differentMediaIcon"));
            this.set("caption", this.get("differentMediaCaption"));
            this.set("tooltip", $i18nService.getString("app.MediaPlayer.PlayPauseNavButton.Tooltip.differentMedia", {
                mediaType: mediaItem.type
            }));
            this.show();
        }
    },

    /**
     * Responds to a click on a hyperlink.
     *
     * @return  {boolean}  Stop propagation
     * @public
     */
    onHyperlinkTap: function() {
        var $appState           = this.app.$.state,
            mediaItem           = this.get("mediaItem"),
            $mediaPlayerManager = this.app.$.mediaPlayerManager,
            $mediaPlayer;

        // No href available
        if (!mediaItem) {
            return true;
        }

        // Same item. Toggle between play and pause.
        if ($mediaPlayerManager.playerHasSameMediaItem(mediaItem)) {
            $mediaPlayer = $appState.get("mediaPlayer");

            if (!$appState.get("mediaPlayerPlaying")) {
                $mediaPlayer.play && $mediaPlayer.play();
            }
            else {
                $mediaPlayer.pause && $mediaPlayer.pause();
            }
        }

        // Different mediaItem. Navigate to it
        else {
            $mediaPlayerManager.userNavToMediaItem(mediaItem);
        }

        return true;
    }

});
